import { useEffect, useMemo, useState } from 'react';
import { GraduationCap, Plus, X } from 'lucide-react';
import { COUNTRIES, REGIONS, STATES_AND_PROVINCES } from '../../data/locations';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { MELT_CHIP, MELT_CHIP_ON, MELT_INPUT } from '../ui/melt';
import { EMPTY_EDUCATION, type Education } from '../../utils/education';
import type { SearchGroup } from './MultiSelect';

// Answer options and fields shared by onboarding and Settings (Profile, Job search).

export const ROLE_GROUPS: Record<string, string[]> = {
  Software: ['Software Engineering', 'Frontend', 'Backend', 'Full Stack', 'Mobile', 'Game Development', 'DevOps / Infrastructure', 'Security', 'QA / Test'],
  'AI & Data': ['Machine Learning / AI', 'Data Engineering', 'Data Science', 'Data Analyst', 'Business Intelligence', 'Quant / Research'],
  Engineering: ['Mechanical', 'Electrical', 'Embedded / Hardware', 'Civil / Structural', 'Chemical / Process', 'Industrial / Manufacturing', 'Aerospace', 'Biomedical', 'Robotics', 'Environmental', 'Architecture'],
  Design: ['Product Design (UX/UI)', 'UX Research', 'Design Engineer (UI)', 'Graphic / Visual Design', 'Brand Design', 'Motion / 3D', 'Industrial Design'],
  Product: ['Product Manager', 'Technical PM', 'Program / Project Manager'],
  Business: ['Marketing', 'Product Marketing', 'Growth', 'Sales', 'Business Development', 'Operations', 'Supply Chain / Logistics', 'Strategy', 'Consulting', 'Finance', 'Investment Banking', 'Accounting', 'Risk / Compliance', 'Actuarial', 'Economics / Research Analyst'],
  Science: ['Research Scientist (lab)', 'Biotech / Pharma R&D', 'Lab / Research Assistant'],
  'People & more': ['Customer Success', 'HR / Recruiting', 'Legal', 'Education / Teaching', 'Communications / Writing', 'Media / Journalism', 'Nonprofit'],
};

// Quick picks for locations. City spellings match public/data/cities.json so
// they don't repeat in the full lists below them.
export const LOCATION_GROUPS: Record<string, string[]> = {
  // Empty label: shown at the top without a heading
  '': [
    'Remote',
    'Canada',
    'United States',
    'Europe',
    'Toronto, ON',
    'Waterloo, ON',
    'Vancouver, BC',
    'Montréal, QC',
    'New York City, NY',
    'San Francisco Bay Area',
    'Seattle, WA',
    'London, United Kingdom',
  ],
};

export const JOB_TYPES = ['Internship', 'Co-op', 'New grad', 'Full-time', 'Part-time', 'Contract', 'Apprenticeship'];
export const CAREER_STAGES = ['Student', 'Recent Graduate', 'Career Switching', 'Experienced Professional'];
// Highest degree (in progress or done). Dream Companies uses it to leave out
// PhD-only / Master's-only / high-school roles (backend/job_matching.py, keep in sync).
export const DEGREES = ['High school', 'Diploma', "Bachelor's", "Master's", 'PhD'];
// Future years for students, past years for graduates and professionals.
export const GRAD_YEARS = Array.from({ length: 51 }, (_, i) => String(2030 - i));
export const SCHOOLS = ['University of Waterloo', 'University of Toronto', 'McGill University', 'University of British Columbia', 'Western University', "Queen's University", 'McMaster University', 'Toronto Metropolitan University'];
export const DISCOVERY_CHANNELS = ['Friend or colleague', 'Content Creator (Social media)', 'Newsletter or blog', 'Community or event', 'Search engine', 'Linkedin', 'Reddit', 'Youtube', 'Instagram', 'Other'];

// Regions, countries, states / provinces and every city over 15k people (GeoNames), largest first.
// Cities load on demand; until they arrive the picker shows the other lists.
export function useLocationSearchGroups(): SearchGroup[] {
  const [cities, setCities] = useState<string[]>([]);
  useEffect(() => {
    fetch('/data/cities.json')
      .then((res) => res.json())
      .then(setCities)
      .catch((error) => console.error('Could not load city list:', error));
  }, []);
  // Memoised: rebuilding it each render would re-index 34k cities per keystroke
  return useMemo(
    () => [
      { label: 'Regions', options: REGIONS },
      { label: 'Countries', options: COUNTRIES, emptyLimit: COUNTRIES.length },
      { label: 'States & provinces', options: STATES_AND_PROVINCES },
      { label: 'Cities', options: cities },
    ],
    [cities]
  );
}

export function Chips({ options, isSelected, onToggle }: { options: string[]; isSelected: (o: string) => boolean; onToggle: (o: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={isSelected(option)}
          onClick={() => onToggle(option)}
          className={cn(MELT_CHIP, isSelected(option) && MELT_CHIP_ON)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export function MeltSelect({ value, onChange, placeholder, options }: { value: string; onChange: (v: string) => void; placeholder: string; options: string[] }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="h-11 rounded-[10px] border-melt-hair px-3.5 text-melt-text1 focus:border-melt-caramel focus:ring-0 focus:shadow-[0_0_0_3px_rgba(217,136,41,.15)] data-[placeholder]:text-melt-text3 [&>svg]:text-melt-text3">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-melt-menu font-grotesk">
        {options.map((o) => (
          <SelectItem key={o} value={o} className="text-melt-text1 focus:bg-melt-text1/5">
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// --- Education ---------------------------------------------------------------------

const MAX_EDUCATION = 4;

/** School, program, degree and grad year for each degree, with "Add another degree" */
export function EducationFields({
  value,
  onChange,
  id,
  labelClassName,
}: {
  value: Education[];
  onChange: (list: Education[]) => void;
  id: string;
  labelClassName: string;
}) {
  const update = (i: number, patch: Partial<Education>) =>
    onChange(value.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  return (
    <div className="flex flex-col gap-2.5">
      <label htmlFor={`${id}-school-0`} className={labelClassName}>Education</label>
      {value.map((entry, i) => (
        <div key={i} className={cn('flex flex-col gap-2.5', i > 0 && 'mt-2 border-t border-melt-hair pt-4')}>
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              <GraduationCap size={17} strokeWidth={1.75} className="absolute left-[13px] top-[13px] text-melt-text3" />
              <input
                id={`${id}-school-${i}`}
                list={`${id}-schools`}
                value={entry.school}
                onChange={(e) => update(i, { school: e.target.value })}
                placeholder="School, e.g. Rice University"
                aria-label="School"
                // Suggestions show as you type; hide Chrome's arrow, which looks like a dropdown
                className={cn(MELT_INPUT, 'pl-[38px] [&::-webkit-calendar-picker-indicator]:!hidden')}
              />
            </div>
            {value.length > 1 && (
              <button
                type="button"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                aria-label="Remove this degree"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] text-melt-text3 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
              >
                <X size={16} strokeWidth={1.75} />
              </button>
            )}
          </div>
          <div className={cn('grid grid-cols-[1.5fr_1fr_1fr] gap-2.5', value.length > 1 && 'mr-[42px]')}>
            <input
              value={entry.program}
              onChange={(e) => update(i, { program: e.target.value })}
              placeholder="Program, e.g. Computer Science"
              aria-label="Program"
              className={MELT_INPUT}
            />
            <MeltSelect value={entry.degree} onChange={(degree) => update(i, { degree })} placeholder="Degree" options={DEGREES} />
            <MeltSelect value={entry.gradYear} onChange={(gradYear) => update(i, { gradYear })} placeholder="Grad year" options={GRAD_YEARS} />
          </div>
        </div>
      ))}
      <datalist id={`${id}-schools`}>
        {SCHOOLS.map((s) => <option key={s} value={s} />)}
      </datalist>
      {value.length < MAX_EDUCATION && (
        <button
          type="button"
          onClick={() => onChange([...value, EMPTY_EDUCATION])}
          className="-ml-2.5 inline-flex h-9 w-fit items-center gap-1.5 rounded-[10px] px-2.5 text-[13.5px] font-medium text-melt-text2 transition-colors hover:bg-melt-text1/[.05] hover:text-melt-text1"
        >
          <Plus size={15} strokeWidth={1.75} />
          Add another degree
        </button>
      )}
    </div>
  );
}
